import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Renders the shopper's recently viewed products by asking the section to render itself
 * for a search by product id (`id:1 OR id:2`), then hydrating the result into the live DOM.
 */
class RecentlyViewedProducts extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.loaded === 'true') return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;

        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    const { sectionId, excludeProductId } = this.dataset;
    const limit = Number(this.dataset.limit) || 4;
    if (!sectionId) return;

    const ids = RecentlyViewed.getProducts(limit + 1)
      .filter((/** @type {string} */ id) => id !== excludeProductId)
      .slice(0, limit);
    if (ids.length === 0) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) return;

      const html = await response.text();
      if (!html.trim().length) return;

      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('Recently viewed error:', error instanceof Error ? error.message : error);
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
